import { useEffect, useState } from 'react'
import { sortBy } from 'lodash-es'

import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import { ISelectOption } from '$/types'
import { SelectDropdownWrapper } from 'common/components/SelectDropdownWrapper'
import { DATEPICKER_FORMAT } from 'constants/dates'

import './style.scss'

interface IHCAMCAToolbar {
  selectedLines: ISelectOption[]
  assessmentDateRange: Array<Date | null>
  selectedClassification: ISelectOption[]
  lineOptions: ISelectOption[]
  classificationOptions: ISelectOption[]
  setSelectedLines: (value: ISelectOption[]) => void
  setAssessmentDateRange: (value: Array<Date | null>) => void
  setSelectedClassification: (value: ISelectOption[]) => void
}

interface IDropdownVisibility {
  route: boolean
  classification: boolean
}

interface ICustomDateSelector {
  type: 'FROM' | 'TO'
  assessmentDateRange: Array<Date | null>
  setAssessmentDateRange: IHCAMCAToolbar['setAssessmentDateRange']
}

const CustomDateSelector = ({
  type,
  assessmentDateRange,
  setAssessmentDateRange
}: ICustomDateSelector) => {
  const indexOfCurrentDateType = type === 'FROM' ? 0 : 1

  return (
    <>
      <p className='ml-3 my-auto text-sm leading-5 text-gray-500'>{type}</p>

      <DatePicker
        format={DATEPICKER_FORMAT}
        value={assessmentDateRange[indexOfCurrentDateType]}
        onChange={(date: Date | null) => {
          const newAssessmentDateRange = [...assessmentDateRange]

          newAssessmentDateRange[indexOfCurrentDateType] = date
          setAssessmentDateRange(newAssessmentDateRange)
        }}
        minDate={
          type === 'TO' ? (assessmentDateRange[0] ?? undefined) : undefined
        }
        slotProps={{
          actionBar: {
            actions: ['clear'],
            sx: {
              justifyContent: 'flex-end',
              paddingRight: 3
            }
          },
          textField: {
            sx: {
              '& .MuiOutlinedInput-root:not(.Mui-focused) fieldset': {
                border: 'none'
              },
              '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                borderWidth: '1px'
              }
            }
          }
        }}
      />
    </>
  )
}

export const ToolbarContent = ({
  selectedLines,
  assessmentDateRange,
  selectedClassification,
  lineOptions,
  classificationOptions,
  setSelectedLines,
  setAssessmentDateRange,
  setSelectedClassification
}: IHCAMCAToolbar) => {
  const [dropdownVisibilityObj, setDropdownVisibilityObj] =
    useState<IDropdownVisibility>({
      route: false,
      classification: false
    })

  useEffect(() => {
    setSelectedClassification([])
  }, [classificationOptions, setSelectedClassification])

  useEffect(() => {
    /* remove '...' from the 'rest' label tag
      example: +3 instead of +3... */
    const restElements = document.getElementsByClassName(
      'ant-select-selection-overflow-item-rest'
    )

    Array.from(restElements).forEach((element) => {
      const spanElement = element.getElementsByClassName(
        'ant-select-selection-item-content'
      )[0]

      spanElement.textContent =
        spanElement.textContent?.replaceAll('.', '') || ''
    })
  }, [selectedLines, selectedClassification])

  function selectedItemsCount(val: number | string): string {
    if (val === 0 || val === undefined) {
      return 'Select...'
    }

    return (val as string) + ' selected'
  }

  const handleDropdownVisibleChange = (
    open: boolean,
    dropDownType: 'route' | 'classification'
  ) => {
    setDropdownVisibilityObj({ ...dropdownVisibilityObj, [dropDownType]: open })
  }

  return (
    <>
      <div className='flex gap-x-4 border-b-4 bg-gray-100 px-6 pb-2 pt-4'>
        <div>
          <label className='font-semibold'>Date State History</label>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className='flex gap-1 mt-2 p-px border border-gray-300 bg-white'>
              <CustomDateSelector
                type='FROM'
                assessmentDateRange={assessmentDateRange}
                setAssessmentDateRange={setAssessmentDateRange}
              />
              <div className='my-auto ml-2 h-7 border-l-2 border-solid border-gray-500'></div>
              <CustomDateSelector
                type='TO'
                assessmentDateRange={assessmentDateRange}
                setAssessmentDateRange={setAssessmentDateRange}
              />
            </div>
          </LocalizationProvider>
        </div>
        <div className='w-1/4'>
          <label className='font-semibold'>Route</label>
          <SelectDropdownWrapper
            className={
              'mt-2 w-full ' +
              (dropdownVisibilityObj['route']
                ? 'custom-select-open'
                : 'custom-select-close')
            }
            onDropdownVisibleChange={(open) => {
              handleDropdownVisibleChange(open, 'route')
            }}
            mode='multiple'
            options={sortBy(lineOptions, [
              (i) => i.label?.toLocaleString().toLowerCase(),
              (i) => i.label
            ])}
            value={selectedLines}
            onChangeSelections={(selections) => {
              setSelectedLines(selections as ISelectOption[])
            }}
            placeholder={selectedItemsCount(selectedLines?.length)}
            maxTagCount='responsive'
            maxTagTextLength={10}
          />
        </div>
        <div className='w-1/4'>
          <label className='font-semibold'>Classification</label>
          <SelectDropdownWrapper
            className={
              'mt-2 w-full ' +
              (dropdownVisibilityObj['classification']
                ? 'custom-select-open'
                : 'custom-select-close')
            }
            onDropdownVisibleChange={(open) => {
              handleDropdownVisibleChange(open, 'classification')
            }}
            mode='multiple'
            options={classificationOptions}
            value={selectedClassification}
            onChangeSelections={(selections) => {
              setSelectedClassification(selections as ISelectOption[])
            }}
            placeholder={selectedItemsCount(selectedClassification?.length)}
            maxTagCount='responsive'
            maxTagTextLength={10}
          />
        </div>
      </div>
    </>
  )
}

export default ToolbarContent
